/**
 * UploadHandler - Manages file uploads for LiveTemplate
 *
 * Handles:
 * - File input detection and change events
 * - Chunked uploads to server via WebSocket
 * - External uploads (S3) via presigned URLs
 * - Progress tracking and callbacks
 * - Upload cancellation
 */

import { S3Uploader } from "./s3-uploader";
import type {
  ExternalUploadMeta,
  FileMetadata,
  UploadChunkMessage,
  UploadCompleteHTTPMessage,
  UploadCompleteMessage,
  UploadEntry,
  UploadHandlerOptions,
  UploadProgressMessage,
  UploadStartMessage,
  UploadStartResponse,
  Uploader,
} from "./types";

export class UploadHandler {
  private entries: Map<string, UploadEntry> = new Map();
  private pendingFiles: Map<string, File[]> = new Map(); // uploadName -> files
  private pendingInputs: Map<string, HTMLInputElement> = new Map(); // uploadName -> triggering input
  private autoUploadConfig: Map<string, boolean> = new Map(); // uploadName -> autoUpload
  private chunkSize: number;
  private uploaders: Map<string, Uploader> = new Map();
  private onProgress?: (entry: UploadEntry) => void;
  private onComplete?: (uploadName: string, entries: UploadEntry[]) => void;
  private onError?: (entry: UploadEntry, error: string) => void;
  private postMultipartUpload?: (
    formData: FormData,
    signal?: AbortSignal
  ) => Promise<void>;
  private isConnected?: () => boolean;
  private postUploadStart?: (
    message: UploadStartMessage,
    signal?: AbortSignal
  ) => Promise<UploadStartResponse>;
  private postUploadComplete?: (
    message: UploadCompleteHTTPMessage,
    signal?: AbortSignal
  ) => Promise<void>;
  private pendingHandshakes: Set<AbortController> = new Set();
  private pendingUploads: Set<AbortController> = new Set(); // in-flight multipart fetches
  private previewUrls: Map<string, string> = new Map(); // uploadName -> object URL
  private inputHandlers: WeakMap<HTMLInputElement, EventListener> = new WeakMap();

  constructor(
    private sendMessage: (message: any) => void,
    options: UploadHandlerOptions = {}
  ) {
    this.chunkSize = options.chunkSize || 256 * 1024; // 256KB default
    this.onProgress = options.onProgress;
    this.onComplete = options.onComplete;
    this.onError = options.onError;
    this.postMultipartUpload = options.postMultipartUpload;
    this.isConnected = options.isConnected;
    this.postUploadStart = options.postUploadStart;
    this.postUploadComplete = options.postUploadComplete;

    // Register default uploaders
    this.uploaders.set("s3", new S3Uploader());
  }

  /** Whether the WebSocket is usable; true when no isConnected probe is wired. */
  private connected(): boolean {
    return this.isConnected ? this.isConnected() : true;
  }

  /**
   * Dispatch a valid upload entry to the transport its mode requires. The server
   * declares the mode per-entry; existing entries that only carry `external`
   * (no mode) fall back to the Direct path for backward compatibility.
   */
  private dispatchUpload(entry: UploadEntry): void {
    switch (entry.mode) {
      case "preview":
        this.uploadPreview(entry);
        return;
      case "proxied":
        void this.uploadMultipart(entry);
        return;
      case "direct":
        if (entry.external) {
          void this.uploadExternal(entry, entry.external);
        } else {
          // Direct requires presign metadata; surface the misconfig instead of
          // silently abandoning the entry.
          const msg = "direct upload mode requires presigned upload metadata";
          entry.error = msg;
          if (this.onError) this.onError(entry, msg);
          this.cleanupEntries(entry.uploadName);
        }
        return;
      case "volume":
      default: {
        // entry.mode is normalized to a concrete mode at ingest (see
        // handleUploadStartResponse), so the default is just the Volume path.
        // Volume stages bytes over the WebSocket chunk transport; with the
        // socket down, fall back to a single multipart POST that the server
        // stages to the field's Dir (#449).
        const connected = this.connected();
        if (!connected) {
          void this.uploadMultipart(entry);
        } else {
          void this.uploadChunked(entry);
        }
      }
    }
  }

  /**
   * Mark an upload finished and run the shared post-completion steps: fire the
   * onComplete callback, clear the file input, and schedule entry cleanup.
   * Callers that talk to the server (chunked/external) send their own
   * upload_complete message first.
   */
  private finishUpload(entry: UploadEntry): void {
    entry.done = true;
    entry.progress = 100;
    // Emit a final 100% tick so every completed path (proxied included) sends it
    // — a progress bar waiting on 100% won't hang. Idempotent for chunked/external
    // which already reach 100% during transfer.
    if (this.onProgress) {
      this.onProgress(entry);
    }
    if (this.onComplete) {
      this.onComplete(entry.uploadName, [entry]);
    }
    this.clearFileInput(entry.uploadName);
    this.cleanupEntries(entry.uploadName);
  }

  /**
   * Initialize upload detection on file inputs with lvt-upload attribute
   */
  initializeFileInputs(container: Element) {
    const inputs = container.querySelectorAll<HTMLInputElement>(
      'input[type="file"][lvt-upload]'
    );

    inputs.forEach((input) => {
      const uploadName = input.getAttribute("lvt-upload");
      if (!uploadName) return;

      // Remove existing listener if any
      const existingHandler = this.inputHandlers.get(input);
      if (existingHandler) {
        input.removeEventListener("change", existingHandler);
      }

      // Create new handler
      const handler = (e: Event) => {
        const files = (e.target as HTMLInputElement).files;
        if (!files || files.length === 0) return;

        // Always send upload_start to get validation and config
        // But only proceed with chunks if autoUpload is true. Pass the triggering
        // input so a Proxied upload can serialize its enclosing form's fields.
        this.startUpload(uploadName, Array.from(files), input);
      };

      input.addEventListener("change", handler);
      // Store handler in WeakMap to prevent memory leaks
      this.inputHandlers.set(input, handler);
    });
  }

  /**
   * Start upload process for selected files
   */
  async startUpload(
    uploadName: string,
    files: File[],
    sourceInput?: HTMLInputElement
  ): Promise<void> {
    // Store files temporarily for when server response arrives
    this.pendingFiles.set(uploadName, files);
    if (sourceInput) {
      this.pendingInputs.set(uploadName, sourceInput);
    } else {
      this.pendingInputs.delete(uploadName);
    }

    // Create file metadata
    const fileMetadata: FileMetadata[] = files.map((file) => ({
      name: file.name,
      type: file.type || "application/octet-stream",
      size: file.size,
    }));

    const startMessage: UploadStartMessage = {
      action: "upload_start",
      upload_name: uploadName,
      files: fileMetadata,
    };

    // Over an open WebSocket the server replies with a separate
    // UploadStartResponse message (routed to handleUploadStartResponse). With
    // the socket down, post the handshake over HTTP and handle the response
    // inline so mode dispatch (and Direct presign) still work.
    const connected = this.connected();
    if (!connected) {
      if (!this.postUploadStart) {
        this.failStart(
          uploadName,
          files,
          "upload unavailable: WebSocket is closed and no HTTP fallback is configured"
        );
        return;
      }
      const controller = new AbortController();
      this.pendingHandshakes.add(controller);
      try {
        const response = await this.postUploadStart(
          startMessage,
          controller.signal
        );
        // Skip if teardown aborted us mid-flight, so we don't create a preview
        // object URL after revokePreviews already ran.
        if (!controller.signal.aborted) {
          await this.handleUploadStartResponse(response);
        }
      } catch (error) {
        this.failStart(
          uploadName,
          files,
          error instanceof Error ? error.message : String(error)
        );
      } finally {
        this.pendingHandshakes.delete(controller);
      }
      return;
    }

    this.sendMessage(startMessage);
  }

  // failStart reports a handshake failure on every selected file and clears the
  // pending set, so a startUpload that can't reach the server isn't silent.
  private failStart(uploadName: string, files: File[], message: string): void {
    this.pendingFiles.delete(uploadName);
    this.pendingInputs.delete(uploadName);
    const onError = this.onError;
    if (!onError) return;
    // Distinct synthetic ids per file (no server entry exists yet) so a consumer
    // keying per-file error state doesn't collapse a multi-file failure.
    files.forEach((file, i) => {
      onError(
        {
          id: `pending-${uploadName}-${i}`,
          file,
          uploadName,
          progress: 0,
          bytesUploaded: 0,
          valid: false,
          done: false,
        },
        message
      );
    });
  }

  /**
   * Handle upload_start response from server
   */
  async handleUploadStartResponse(
    response: UploadStartResponse
  ): Promise<void> {
    const { upload_name, entries: entryInfos } = response;

    // Store autoUpload configuration from first entry
    if (entryInfos.length > 0) {
      this.autoUploadConfig.set(upload_name, entryInfos[0].auto_upload);
    }

    // Get pending files for this upload
    const files = this.pendingFiles.get(upload_name);
    if (!files) {
      console.error(`No pending files found for upload: ${upload_name}`);
      return;
    }

    // Clear pending files
    this.pendingFiles.delete(upload_name);

    // The input that triggered this upload (if any), so a Proxied entry can
    // serialize its enclosing form's fields. Consumed once, like pendingFiles.
    const sourceInput = this.pendingInputs.get(upload_name);
    this.pendingInputs.delete(upload_name);

    // Build a map from file name to file object for lookup
    const fileMap = new Map<string, File>();
    for (const file of files) {
      fileMap.set(file.name, file);
    }

    // Create upload entries
    const entries: UploadEntry[] = [];

    for (const info of entryInfos) {
      const file = fileMap.get(info.client_name);

      if (!file) {
        console.warn(
          `No file found for entry ${info.entry_id} (client_name: ${info.client_name})`
        );
        continue;
      }

      const entry: UploadEntry = {
        id: info.entry_id,
        file,
        uploadName: upload_name,
        progress: 0,
        bytesUploaded: 0,
        valid: info.valid,
        done: false,
        error: info.error,
        // Normalize legacy responses (no mode) at the ingest boundary: a
        // presigned entry is Direct, otherwise server-side Volume. Downstream
        // dispatch then only ever sees a concrete mode.
        mode: info.mode ?? (info.external ? "direct" : "volume"),
        external: info.external,
        sourceInput,
      };

      this.entries.set(entry.id, entry);
      entries.push(entry);

      // Handle invalid entries
      if (!info.valid) {
        if (this.onError && info.error) {
          this.onError(entry, info.error);
        }
        continue;
      }

      // Only start upload immediately if autoUpload is true; otherwise the entry
      // waits for an explicit form-submit trigger.
      if (info.auto_upload) {
        this.dispatchUpload(entry);
      }
    }
  }

  /**
   * Upload file using external uploader (S3, etc.)
   */
  private async uploadExternal(
    entry: UploadEntry,
    meta: ExternalUploadMeta
  ): Promise<void> {
    this.warnIfMarkedFieldsCannotTravel(entry, "direct-to-storage");

    try {
      const uploader = this.uploaders.get(meta.uploader);
      if (!uploader) {
        throw new Error(`Unknown uploader: ${meta.uploader}`);
      }

      // Snapshot the transport BEFORE the (potentially slow) PUT, so the
      // completion uses the same transport the upload_start handshake did — the
      // side that holds the entry. If we re-checked after the PUT, a WS that
      // dropped or reconnected mid-upload could make the two disagree: a WS-start
      // entry completed over HTTP (or vice-versa) targets a registry that never
      // had it. Volume likewise picks its transport up front, at dispatch time
      // (a mid-transfer drop within either path is out of scope here).
      const connected = this.connected();

      // Start external upload with progress callback
      await uploader.upload(entry, meta, this.onProgress);

      // Notify the server of completion. Over WebSocket the server still holds
      // the presigned entry, so an entry_ids ack suffices. With the socket down
      // the per-request registry is gone, so re-send the entry metadata + the
      // ref we uploaded to and let the server reconstruct the entry (#448).
      if (!connected) {
        if (!this.postUploadComplete) {
          // The bytes are already on the CDN, but a sendMessage over the dead
          // socket would silently drop the completion — surface it instead.
          const msg =
            "upload_complete unavailable: WebSocket is down and no HTTP transport configured";
          entry.error = msg;
          if (this.onError) this.onError(entry, msg);
          this.cleanupEntries(entry.uploadName);
          return;
        }
        const httpComplete: UploadCompleteHTTPMessage = {
          action: "upload_complete",
          upload_name: entry.uploadName,
          entries: [
            {
              client_name: entry.file.name,
              type: entry.file.type,
              size: entry.file.size,
              ref: meta.url,
            },
          ],
        };
        await this.postUploadComplete(httpComplete, entry.abortController?.signal);
      } else {
        const completeMessage: UploadCompleteMessage = {
          action: "upload_complete",
          upload_name: entry.uploadName,
          entry_ids: [entry.id],
        };
        this.sendMessage(completeMessage);
      }
      this.finishUpload(entry);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      entry.error = errorMsg;
      if (this.onError) {
        this.onError(entry, errorMsg);
      }
      // Schedule cleanup after error
      this.cleanupEntries(entry.uploadName);
    }
  }

  /**
   * Upload a file as a single multipart POST to the live URL, carrying the
   * upload_<name>_complete action and any lvt-upload-with fields. The server
   * routes the part by the field's configured mode: a Proxied field streams the
   * bytes to OnUpload (zero local-disk staging), a Volume-with-Dir field stages
   * them to Dir. Independent of the WebSocket — Proxied always uses this, and
   * Volume falls back to it when the socket is down (#449).
   */
  private async uploadMultipart(entry: UploadEntry): Promise<void> {
    if (!this.postMultipartUpload) {
      const msg =
        "Multipart upload unavailable: no multipart transport configured";
      entry.error = msg;
      if (this.onError) this.onError(entry, msg);
      this.cleanupEntries(entry.uploadName);
      return;
    }

    entry.abortController = new AbortController();
    this.pendingUploads.add(entry.abortController);
    // A single fetch has no native upload progress, so emit a start event (0%)
    // and let finishUpload emit 100% — a progress bar won't sit frozen with no
    // signal at all.
    if (this.onProgress) this.onProgress(entry);
    try {
      const formData = new FormData();
      // Write value fields BEFORE the file part so the server resolves the
      // action regardless of multipart part ordering.
      formData.set("lvt-action", `upload_${entry.uploadName}_complete`);
      // Then the fields marked to travel with the upload (e.g. a record id), so
      // the server can associate the streamed bytes with a record in OnUpload.
      this.appendFormFields(formData, entry);
      // set(), not append(): if a marked value field happens to share the upload
      // field's name, the file replaces it. The file part is the whole point of
      // the request, so it wins the collision.
      formData.set(entry.uploadName, entry.file, entry.file.name);

      await this.postMultipartUpload(formData, entry.abortController.signal);
      this.finishUpload(entry);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      entry.error = errorMsg;
      if (this.onError) {
        this.onError(entry, errorMsg);
      }
      this.cleanupEntries(entry.uploadName);
    } finally {
      if (entry.abortController) this.pendingUploads.delete(entry.abortController);
      // Release the DOM reference once the upload is done — the entry may linger
      // until the cleanup timer fires, and only uploadMultipart ever reads it.
      entry.sourceInput = undefined;
    }
  }

  /**
   * appendFormFields serializes the opted-in value fields of the form enclosing
   * the upload's triggering input into formData, ahead of the file part — so a
   * Proxied OnUpload handler can read them (e.g. a record id) mid-stream. A
   * no-op when the input is outside a form or nothing is marked.
   *
   * Opt-in, not opt-out: only fields carrying `lvt-upload-with` travel. A
   * Proxied upload auto-fires on file selection rather than on an explicit
   * submit, so the user never gets a submit-time moment to notice what leaves
   * the page. A denylist fails open — an unanticipated sensitive control (a CSRF
   * token, a hidden secret, an `autocomplete="current-password"` text input)
   * rides along silently. An allowlist fails closed: forget to mark the record
   * id and OnUpload sees a missing field, which is a visible bug rather than a
   * silent leak.
   *
   * The mark is form-scoped, not upload-scoped: a marked field travels with
   * every upload fired from its form. Marking is by *name*, so marking any one
   * member of a radio/checkbox group opts in the whole group — the browser's
   * own successful-control rules then decide which values are actually sent.
   *
   * Values are read here, at upload time (after the upload_start round-trip), not
   * at file-selection time, so edits made between selecting the file and the
   * upload completing are reflected — the freshest form state wins.
   *
   * When one selection carries several files, each is POSTed as its own request,
   * so the marked fields are re-serialized into every one — each request reaches
   * OnUpload independently and must be self-describing.
   */
  private appendFormFields(formData: FormData, entry: UploadEntry): void {
    const form = entry.sourceInput?.form;
    if (!form) return;
    // Collect the opted-in names first, then let FormData do the serializing, so
    // successful-control semantics (unchecked boxes, disabled controls, multi
    // selects) stay the browser's job rather than ours.
    const optedIn = new Set<string>();
    for (const el of Array.from(form.elements)) {
      const name = (el as HTMLInputElement).name;
      if (name && el.hasAttribute("lvt-upload-with")) optedIn.add(name);
    }
    if (optedIn.size === 0) return;
    for (const [name, value] of new FormData(form).entries()) {
      // lvt-action is reserved for the action set by the caller, and a marked
      // file input would double-send its bytes as a value field.
      if (!optedIn.has(name) || name === "lvt-action" || value instanceof File) {
        continue;
      }
      formData.append(name, value);
    }
  }

  /**
   * Preview mode: keep the file on the device and show it locally via an object
   * URL. Nothing is uploaded — the server already has the file's metadata from
   * the upload_start handshake. The blob URL is re-applied after server
   * re-renders by hydratePreviews().
   */
  private uploadPreview(entry: UploadEntry): void {
    // Revoke any prior preview for this field to avoid leaking object URLs.
    const prev = this.previewUrls.get(entry.uploadName);
    if (prev) URL.revokeObjectURL(prev);

    const url = URL.createObjectURL(entry.file);
    this.previewUrls.set(entry.uploadName, url);
    this.applyPreview(entry.uploadName, url);

    // Share the completion path with every other mode (progress 100% tick,
    // onComplete, input clear, cleanup). The blob URL lives in previewUrls, not
    // the input, so it survives the clear.
    this.finishUpload(entry);
  }

  /** Point every preview placeholder for uploadName at the given object URL. */
  private applyPreview(uploadName: string, url: string): void {
    // Escape the field name so a name with a quote/bracket can't break or inject
    // into the selector. Prefer CSS.escape; without it, only proceed for a simple
    // identifier (the normal case) and otherwise skip rather than run a
    // possibly-broken selector (hydratePreviews still re-applies via attr reads).
    let safeName: string;
    if (typeof CSS !== "undefined" && CSS.escape) {
      safeName = CSS.escape(uploadName);
    } else if (/^[\w-]+$/.test(uploadName)) {
      safeName = uploadName;
    } else {
      return;
    }
    const els = document.querySelectorAll<HTMLElement>(
      `[data-lvt-upload-preview="${safeName}"]`
    );
    els.forEach((el) => {
      if (el instanceof HTMLImageElement) {
        if (el.src !== url) el.src = url;
      } else if (el.getAttribute("src") !== url) {
        el.setAttribute("src", url);
      }
    });
  }

  /**
   * Re-attach Preview-mode blob URLs after a DOM morph. The server re-renders
   * the {{.lvt.UploadPreview}} placeholder with an empty src, so this restores
   * the local object URL the visitor selected. Called by the client post-update.
   */
  hydratePreviews(root: Element): void {
    const apply = (el: Element) => {
      const name = el.getAttribute("data-lvt-upload-preview");
      if (!name) return;
      const url = this.previewUrls.get(name);
      if (!url) return;
      if (el instanceof HTMLImageElement) {
        if (el.src !== url) el.src = url;
      } else if (el.getAttribute("src") !== url) {
        el.setAttribute("src", url);
      }
    };
    // querySelectorAll skips the root itself, so match it explicitly.
    if (root.matches("[data-lvt-upload-preview]")) apply(root);
    root
      .querySelectorAll<HTMLElement>("[data-lvt-upload-preview]")
      .forEach(apply);
  }

  /** Revoke all preview object URLs. Called on teardown to avoid leaks. */
  revokePreviews(): void {
    // Abort in-flight offline handshakes and proxied upload fetches first, so a
    // late resolution doesn't create a preview URL or apply a tree update after
    // the client has torn down.
    for (const controller of this.pendingHandshakes) {
      controller.abort();
    }
    this.pendingHandshakes.clear();
    for (const controller of this.pendingUploads) {
      controller.abort();
    }
    this.pendingUploads.clear();
    for (const url of this.previewUrls.values()) {
      URL.revokeObjectURL(url);
    }
    this.previewUrls.clear();
  }

  /**
   * Warn when a form marks fields with lvt-upload-with but the upload is leaving
   * by a transport that carries no form fields. Only the multipart POST carries
   * them: the chunked WebSocket path sends bytes and entry ids, and the Direct
   * path PUTs straight to storage and completes with a metadata-only message —
   * in both cases the server builds the action's context from an empty map.
   * Without this the fields simply never arrive and the handler sees empty
   * values, with nothing in the markup to suggest why.
   *
   * transport names the path taken, since the remedy differs: a Volume upload
   * does carry fields when it falls back to multipart, while Direct never does
   * on any path. Tracked as livetemplate/livetemplate#508.
   */
  private warnIfMarkedFieldsCannotTravel(
    entry: UploadEntry,
    transport: "chunked WebSocket" | "direct-to-storage"
  ): void {
    const form = entry.sourceInput?.form;
    if (!form) return;
    const marked = Array.from(form.elements)
      .filter((el) => el.hasAttribute("lvt-upload-with"))
      .map((el) => (el as HTMLInputElement).name)
      .filter(Boolean);
    if (marked.length === 0) return;
    const remedy =
      transport === "chunked WebSocket"
        ? `Fields travel on the multipart path only — Proxied uploads, and this ` +
          `field when the socket is down.`
        : `Fields travel on the multipart path only, which Direct never uses; ` +
          `read the context from controller state instead.`;
    console.warn(
      `Upload "${entry.uploadName}" is using the ${transport} transport, which ` +
        `does not carry form fields — ${marked.join(", ")} marked lvt-upload-with ` +
        `will not reach the upload_${entry.uploadName}_complete handler. ` +
        `${remedy} See livetemplate/livetemplate#508.`
    );
  }

  /**
   * Upload file in chunks via WebSocket
   */
  private async uploadChunked(entry: UploadEntry): Promise<void> {
    const { file, id } = entry;
    let offset = 0;

    this.warnIfMarkedFieldsCannotTravel(entry, "chunked WebSocket");

    // Create abort controller for cancellation
    entry.abortController = new AbortController();

    try {
      while (offset < file.size) {
        // Check if upload was cancelled
        if (entry.abortController.signal.aborted) {
          throw new Error("Upload cancelled");
        }
        // Read chunk
        const end = Math.min(offset + this.chunkSize, file.size);
        const chunk = file.slice(offset, end);

        // Convert to base64
        const base64 = await this.fileToBase64(chunk);

        // Send chunk
        const chunkMessage: UploadChunkMessage = {
          action: "upload_chunk",
          entry_id: id,
          chunk_base64: base64,
          offset,
          total: file.size,
        };

        this.sendMessage(chunkMessage);

        // Update progress
        offset = end;
        entry.bytesUploaded = offset;
        entry.progress = Math.round((offset / file.size) * 100);

        if (this.onProgress) {
          this.onProgress(entry);
        }
      }

      // Send complete message
      entry.done = true;
      const completeMessage: UploadCompleteMessage = {
        action: "upload_complete",
        upload_name: entry.uploadName,
        entry_ids: [id],
      };

      this.sendMessage(completeMessage);
      this.finishUpload(entry);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      entry.error = errorMsg;
      if (this.onError) {
        this.onError(entry, errorMsg);
      }
      // Schedule cleanup after error
      this.cleanupEntries(entry.uploadName);
    }
  }

  /**
   * Handle progress message from server (for chunked uploads)
   */
  handleProgressMessage(message: UploadProgressMessage): void {
    const entry = this.entries.get(message.entry_id);
    if (!entry) return;

    entry.progress = message.progress;
    entry.bytesUploaded = message.bytes_recv;

    if (this.onProgress) {
      this.onProgress(entry);
    }
  }

  /**
   * Cancel upload
   */
  cancelUpload(entryId: string): void {
    const entry = this.entries.get(entryId);
    if (!entry) return;

    // Abort external upload if in progress
    if (entry.abortController) {
      entry.abortController.abort();
    }

    // Send cancel message to server
    this.sendMessage({
      action: "cancel_upload",
      entry_id: entryId,
    });

    // Remove entry
    this.entries.delete(entryId);
  }

  /**
   * Get all entries for an upload name
   */
  getEntries(uploadName: string): UploadEntry[] {
    const entries: UploadEntry[] = [];
    for (const entry of this.entries.values()) {
      if (entry.uploadName === uploadName) {
        entries.push(entry);
      }
    }
    return entries;
  }

  /**
   * Trigger upload for all pending entries (used when autoUpload is false)
   * Called by LiveTemplate client on form submit
   */
  triggerPendingUploads(uploadName: string): void {
    // Get all entries for this upload that haven't started yet
    const pendingEntries: UploadEntry[] = [];
    for (const entry of this.entries.values()) {
      if (
        entry.uploadName === uploadName &&
        entry.progress === 0 &&
        !entry.done &&
        !entry.error
      ) {
        pendingEntries.push(entry);
      }
    }

    // Start uploads
    for (const entry of pendingEntries) {
      this.dispatchUpload(entry);
    }
  }

  /**
   * Register custom uploader
   */
  registerUploader(name: string, uploader: Uploader): void {
    this.uploaders.set(name, uploader);
  }

  /**
   * Clear file input to prevent re-upload of the same file
   * Called after successful upload completion
   */
  private clearFileInput(uploadName: string): void {
    // Find all file inputs with this upload name
    const inputs = document.querySelectorAll<HTMLInputElement>(
      `input[type="file"][lvt-upload="${uploadName}"]`
    );

    inputs.forEach((input) => {
      // Clear the file input value
      input.value = '';
    });
  }

  /**
   * Clean up completed or errored upload entries to prevent memory leaks
   * Automatically called after completion/error, but can be called manually
   * @param uploadName - Optional upload name to clean specific uploads
   * @param delay - Optional delay in ms before cleanup (default: 5000ms)
   */
  cleanupEntries(uploadName?: string, delay: number = 5000): void {
    setTimeout(() => {
      const entriesToRemove: string[] = [];

      for (const [id, entry] of this.entries) {
        // Skip if uploadName is specified and doesn't match
        if (uploadName && entry.uploadName !== uploadName) continue;

        // Remove completed or errored entries
        if (entry.done || entry.error) {
          entriesToRemove.push(id);
        }
      }

      for (const id of entriesToRemove) {
        this.entries.delete(id);
      }
    }, delay);
  }

  /**
   * Convert File/Blob to base64 string
   */
  private fileToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix
        const base64 = result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
